/* eslint-disable jsx-a11y/control-has-associated-label */
/* eslint-disable react/no-unstable-nested-components */
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import ContainerBody from 'src/components/ui/ContainerOverlay/ContainerBody/ContainerBody';
import { useOutsideClick, useEventListeners } from 'src/hooks/utils';
import classNames from 'classnames';
import { toast } from 'react-toastify';
import { useConnectorMappingMutations, useConnectorProductMappingQuery } from '../../services/connector';
import styles from './IntegrationsMappingOverlay.module.scss';
import Icon from '../../components/ui/Icon/Icon';
import Chat from '../../components/layouts/Chat/Chat';
import Divider from '../../components/ui/Divider/Divider';
import { useNavigationItems } from '../../hooks/useNavigationItems';
import { SelectSingleProductFromInventory } from '../../components/layouts/SelectSingleProduct/SelectSingleProduct';
import Button, { ButtonToneProps } from '../../components/ui/Button/Button';
import { SelectedProductsProduct } from '../../components/layouts/SelectProducts/SelectProducts';
import ProductImage from '../../components/ui/ProductImage/ProductImage';

export interface IntegrationsMappingOverlayProps {
  mappingId: number;
  onClose: () => void;
}

export const IntegrationsMappingOverlay: React.FC<IntegrationsMappingOverlayProps> = ({ mappingId, onClose }) => {
  const { t } = useTranslation();
  const [showSelectProducts, setShowSelectProducts] = useState(false);
  const mainRef = React.useRef<HTMLDivElement>(null);
  const { findAccessRights } = useNavigationItems();
  const chatAccess = findAccessRights('chatter');

  const { data, refetch } = useConnectorProductMappingQuery(mappingId);

  const { updateProductMappingMutation, deleteProductMapping } = useConnectorMappingMutations();

  const handleProductMappingUpdate = (product: SelectedProductsProduct | null) => {
    if (!product) return;

    const payload = {
      product_id: product.product_id,
      chat: null,
    };

    updateProductMappingMutation.mutate(
      {
        params: { path: { mapping_id: mappingId } },
        body: payload,
      },
      {
        onSuccess: () => {
          toast.success(t('integrations.product_update_successful'));
          refetch();
        },
      },
    );
  };

  const handleProductMappingDelete = () => {
    deleteProductMapping.mutate(
      {
        params: { path: { mapping_id: mappingId } },
      },
      {
        onSuccess: () => {
          toast.success(t('integrations.product_update_successful'));
          refetch();
        },
      },
    );
  };

  useOutsideClick(mainRef, e => {
    const event = e as MouseEvent;
    if (mainRef.current && !mainRef.current.contains(event.target as Node)) {
      onClose();
    }
  });

  useEventListeners([{ event: 'keydown' }], e => {
    const event = e as KeyboardEvent;
    if (event.key === 'Escape') {
      onClose();
    }
  });

  return (
    <div
      ref={mainRef}
      data-overlay-handles-outside="true"
      data-overlay-handles-escape="true"
      data-overlay-type="inbound-package"
      style={{ height: '100%', overflow: 'auto' }}>
      <ContainerBody>
        {data && (
          <>
            {!showSelectProducts && (
              <>
                <div className="flex flex-col gap-y-8 pb-40">
                  <div className="flex gap-x-16 items-center">
                    <h2 className="text-full typo-title-36">{data.name}</h2>
                  </div>
                </div>
                <div className={classNames(styles.tableWrapper, 'mb-16')}>
                  <table>
                    <thead>
                      <tr>
                        <th colSpan={1}>{t('general.name')}</th>
                        <th colSpan={1}>{t('general.barcode')}</th>
                        <th />
                      </tr>
                    </thead>

                    <tbody>
                      {data?.product_id ? (
                        <tr key={data.product_id} className={styles.linked_cases}>
                          <td>
                            <div className="flex flex-row items-center gap-12">
                              <ProductImage id={data.product_id as number} />
                              {data.product_name || ''}
                            </div>
                          </td>
                          <td>
                            <span className={`${classNames(styles.cellTitle, styles.code)} justify-start`}>
                              {data?.barcode ?? '-'}
                            </span>
                          </td>
                          <td>
                            <Icon
                              name="close"
                              width={24}
                              height={24}
                              className={styles.deleteIcon}
                              onClick={handleProductMappingDelete}
                            />
                          </td>
                        </tr>
                      ) : (
                        <tr className="w-full">
                          <td className="w-1/3" />
                          <td className="flex flex-row justify-center typo-text-14 text-faded">
                            {t('general.no_data')}
                          </td>
                          <td className="w-1/3" />
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>
                <div className="flex justify-end mt-16">
                  <Button
                    tone={ButtonToneProps.SECONDARY}
                    title={t('integrations.link_product')}
                    onClick={() => setShowSelectProducts(true)}
                  />
                </div>
              </>
            )}
            {showSelectProducts && (
              <SelectSingleProductFromInventory
                defaultValue={data.product_id}
                onConfirm={product => {
                  handleProductMappingUpdate(product);
                  setShowSelectProducts(false);
                }}
              />
            )}
            {!showSelectProducts && (chatAccess?.read || chatAccess?.read_write) && (
              <>
                <Divider className="my-24" />
                <Chat
                  model="df.mapping.product"
                  res_id={Number(mappingId)}
                  withCreateEditMessage={chatAccess?.read_write}
                />
              </>
            )}
          </>
        )}
      </ContainerBody>
    </div>
  );
};

export default IntegrationsMappingOverlay;
